import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import signInClient from "../auth";
import Button from "../componentLibrary/Button";
import { validatePassword } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";

const SetNewPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { username, password } = (location.state || {}) as {
    username: string;
    password: string;
  };

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login: authLogin } = useAuth();

  const handleSetNewPassword = async () => {
    setIsLoading(true);
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    const { isError, errorMessage } = validatePassword(newPassword);

    if (isError) {
      setGeneralError(errorMessage);
      setIsLoading(false);
      return;
    }
    try {
      const response = await signInClient.post(
        "/sign-in/set-new-password-first-time",
        {
          username,
          password,
          newPassword,
        },
      );

      // Tokens are now set as HttpOnly cookies by the server
      const userData = response.data.user;
      authLogin({
        sub: userData.sub,
        name: userData.name,
        email: userData.email,
        role: userData.role,
      });

      setIsLoading(false);

      navigate("/tutorial");
    } catch {
      setIsLoading(false);
      setGeneralError("An error occurred while setting the new password.");
    }
  };

  return (
    <div className="flex flex-col mx-auto w-80 mt-24 space-y-4">
      <div className="flex flex-col items-center">
        <img
          src={p4lLogo}
          alt="Psych For Life logo"
          className="w-32 h-32 mb-2"
        />
        <p className="text-gray-700 text-3xl">Psych For Life&reg;</p>
      </div>
      <p className="text-gray-700 text-2xl">Set New Password</p>
      <TextField
        label="New Password"
        variant="outlined"
        fullWidth
        type={showNewPassword ? "text" : "password"}
        name="newPassword"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowNewPassword(!showNewPassword)}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Confirm New Password"
        variant="outlined"
        fullWidth
        type={showConfirmPassword ? "text" : "password"}
        name="confirmPassword"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSetNewPassword();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {generalError && <p className="text-red-500">{generalError}</p>}
      {passwordError && <p className="text-red-500">{passwordError}</p>}
      <Button
        className="w-full"
        onClick={handleSetNewPassword}
        loading={isLoading}
      >
        Set New Password
      </Button>
    </div>
  );
};

export default SetNewPasswordPage;
